/** Accept.js plumbing: loading the script and turning input into a one-time token. */
export interface GatewayConfig {
  gateway: string
  api_login_id: string
  public_client_key: string
  script_url: string
}
export interface Nonce { descriptor: string; value: string }

interface AcceptResponse {
  messages?: { resultCode?: string; message?: { text: string }[] }
  opaqueData?: { dataDescriptor: string; dataValue: string }
}
declare global {
  interface Window { Accept?: { dispatchData: (data: unknown, cb: (r: AcceptResponse) => void) => void } }
}

let scriptLoad: Promise<void> | null = null
/** Add the Accept.js script tag once per page load. */
export function loadAcceptJs(url: string): Promise<void> {
  if (window.Accept) return Promise.resolve()
  if (scriptLoad) return scriptLoad
  scriptLoad = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script')
    s.src = url
    s.async = true
    s.onload = () => (window.Accept ? resolve() : reject(new Error('Accept.js did not initialise')))
    s.onerror = () => reject(new Error('Could not load the card form script'))
    document.head.appendChild(s)
  }).catch((e) => { scriptLoad = null; throw e })
  return scriptLoad
}

/** "MM/YY" or "MM/YYYY" to the two fields Accept.js wants. */
export function splitExpiry(raw: string): { month: string; year: string } | null {
  const m = raw.replace(/\s/g, '').match(/^(\d{1,2})\/?(\d{2}|\d{4})$/)
  if (!m) return null
  const month = m[1].padStart(2, '0')
  const year = m[2].length === 2 ? `20${m[2]}` : m[2]
  if (Number(month) < 1 || Number(month) > 12) return null
  return { month, year }
}

export function dispatch(cfg: GatewayConfig, payload: Record<string, unknown>, what: string): Promise<Nonce> {
  return new Promise((resolve, reject) => {
    if (!window.Accept) { reject(new Error('The card form is not ready yet.')); return }
    window.Accept.dispatchData(
      { authData: { clientKey: cfg.public_client_key, apiLoginID: cfg.api_login_id }, ...payload },
      (r) => {
        if (r?.messages?.resultCode === 'Error') {
          reject(new Error(r.messages.message?.map((m) => m.text).join(' ') || `${what} details rejected`))
          return
        }
        if (!r?.opaqueData?.dataValue) { reject(new Error(`The ${what} form did not return a payment token. Try again.`)); return }
        resolve({ descriptor: r.opaqueData.dataDescriptor, value: r.opaqueData.dataValue })
      },
    )
  })
}

